import React from 'react';
import { pushGaData } from '../common';
import { getPathnameWithoutLang, getWithEndingSlash, toPathnameWithEndingSlash, setIsFromSelfServiceCookie } from '../utils';
import HeaderJsxParser from './HeaderJsxParser';
import { formatPrice } from './util/PriceUtil';
export const mapMenuItemsToNavItems = (props) => {
    if (!props.menuItems) {
        return [];
    }
    return filterMenuItemsBasedOnClientType(props.menuItems, props).map((menuItem) => createTopLevelItem(menuItem, props));
};
const filterMenuItemsBasedOnClientType = (menuItems, props) => {
    const { profileTypes, headerSegment, customerType } = props;
    const customerProfileTypes = profileTypes ? profileTypes.slice() : ['all'];
    if (customerType && isValidLoggedInType(headerSegment, customerType)) {
        customerProfileTypes.push('logged_in');
    }
    return menuItems.filter((menuItem) => {
        if (!menuItem.clientProfileTypes) {
            return true;
        }
        return menuItem.clientProfileTypes.slice().some((type) => customerProfileTypes.includes(type));
    });
};
const isValidLoggedInType = (headerSegment, customerType) => {
    const businessCustomerOnBusinessPage = headerSegment === 'business' && customerType === 'B2B';
    const privateCustomerOnPrivatePage = headerSegment === 'private' && customerType === 'B2C';
    return businessCustomerOnBusinessPage || privateCustomerOnPrivatePage;
};
const createTopLevelItem = (menuItem, props) => {
    var _a;
    const { currentPath, loggedIn, app, unpaidBill } = props;
    const current = isCurrentTopLevelItem(menuItem, currentPath, app);
    const crumbs = [menuItem];
    const isSecure = (menuItem.requireAuth && !loggedIn) || undefined;
    return {
        id: (_a = menuItem.id) === null || _a === void 0 ? void 0 : _a.toString(),
        label: getMenuItemLabel(menuItem),
        mobileLabel: getMenuItemLabel(menuItem, true),
        icon: getIcon(menuItem),
        color: getColor(menuItem),
        url: getMenuItemUrl(menuItem, props),
        isCurrent: current,
        ...getSecondaryIconProps(menuItem, unpaidBill, loggedIn),
        children: menuItem.children && {
            columns: filterMenuItemsBasedOnClientType(menuItem.children, props).map((item) => mapColumns(item, [...crumbs, menuItem], current || menuItem.path === '#', isSecure, props)),
            banners: menuItem.offer && React.createElement(HeaderJsxParser, { content: menuItem.offer }),
        },
        onClick: (event) => props.onClick(event, menuItem, crumbs),
        onToggle: (isOpen) => props.onToggle(isOpen, menuItem),
    };
};
const mapColumns = (menuItem, crumbs, parentIsCurrent, parentIsSecure, props) => ({
    lists: menuItem.children
        ? filterMenuItemsBasedOnClientType(menuItem.children, props).map((item) => mapItems(item, menuItem, crumbs, parentIsCurrent, parentIsSecure, props))
        : [],
    hasBorder: menuItem.divider,
});
const mapItems = (menuItem, column, crumbs, parentIsCurrent, parentIsSecure, props) => ({
    items: menuItem.children
        ? filterMenuItemsBasedOnClientType(menuItem.children, props).map((item) => mapItem(item, menuItem, column, crumbs, parentIsCurrent, parentIsSecure, props))
        : [],
    hasBorder: menuItem.divider,
    title: getMenuItemLabel(menuItem),
});
const mapItem = (menuItem, itemsHeading, column, crumbs, parentIsCurrent, parentIsSecure, props) => {
    var _a;
    return {
        id: (_a = menuItem.id) === null || _a === void 0 ? void 0 : _a.toString(),
        label: getMenuItemLabel(menuItem),
        isSecure: !parentIsSecure && menuItem.requireAuth && !props.loggedIn ? true : undefined,
        target: menuItem.target,
        url: getMenuItemUrl(menuItem, props),
        color: getColor(menuItem, itemsHeading, column),
        tag: getMenuItemTag(menuItem, props.unpaidBill),
        image: menuItem.xfn ? { src: menuItem.xfn, width: 16, role: 'presentation' } : null,
        isCurrent: parentIsCurrent && isMatchingPath(menuItem, props.currentPath),
        onClick: (event) => handleMenuItemClick(event, menuItem, crumbs, props),
    };
};
const getMenuItemTag = (menuItem, unpaidBill) => {
    if (menuItem.tagContent) {
        return { text: menuItem.tagContent, purpose: menuItem.tagColour };
    }
    if ((menuItem.url.endsWith('/arved') || isBillsChildMenuItem(menuItem)) && (unpaidBill === null || unpaidBill === void 0 ? void 0 : unpaidBill.sum) > 0) {
        return {
            text: formatPrice(unpaidBill.sum, true),
            purpose: (unpaidBill.hasDebt ? 'error' : 'tertiary'),
            size: 'tiny',
        };
    }
    return null;
};
const getSecondaryIconProps = (menuItem, unpaidBill, loggedIn) => {
    const isSecure = (menuItem.requireAuth && !loggedIn) || undefined;
    if (isSecure) {
        return { secondaryIcon: '#lock' };
    }
    if (isSelfServiceMenuItem(menuItem) || isBillsMenuItem(menuItem)) {
        if ((unpaidBill === null || unpaidBill === void 0 ? void 0 : unpaidBill.sum) > 0) {
            return {
                secondaryIcon: '#alert-round',
                secondaryIconPurpose: (unpaidBill.hasDebt ? 'text-error' : 'text-primary'),
            };
        }
    }
    return { secondaryIcon: null };
};
const handleMenuItemClick = (event, menuItem, crumbs, props) => {
    const menuItemLabel = getMenuItemLabel(menuItem);
    const parentItemLabel = getMenuItemLabel(crumbs[crumbs.length - 1]);
    pushGaData({ eventAction: `Header Submenu Click - ${parentItemLabel}`, eventLabel: menuItemLabel });
    setIsFromSelfServiceCookie(isContactsChildMenuItem(menuItem));
    props.onClick(event, menuItem, crumbs);
};
const getIcon = (menuItem) => (menuItem.icon ? `#${menuItem.icon}` : null);
const isEmployeeMenuItem = (menuItem) => { var _a; return (_a = menuItem === null || menuItem === void 0 ? void 0 : menuItem.clientProfileTypes) === null || _a === void 0 ? void 0 : _a.includes('customer_service_employee'); };
const getColor = (menuItem, itemsHeading, column) => {
    return isEmployeeMenuItem(menuItem) || isEmployeeMenuItem(column) || isEmployeeMenuItem(itemsHeading) ? 'employee' : null;
};
const isCurrentTopLevelItem = (menuItem, currentPath, app) => {
    return ((app !== 'frontline' && isIncludedInPath(menuItem, currentPath)) ||
        (app === 'frontline' && hasChildWithPath(menuItem, currentPath)) ||
        isSelfServiceAppAndSelfServiceMenuItem(menuItem, app) ||
        isShopAppAndShopMenuItem(menuItem, app) ||
        isActiveOnHelpMenuItem(menuItem, currentPath, app));
};
const hasChildWithPath = (menuItem, currentPath) => {
    const getPaths = (item) => (item.children || []).reduce((paths, child) => [...paths, child.path, ...getPaths(child)], [menuItem.path]);
    const menuItemChildrenPaths = getPaths(menuItem).filter((path) => !!path && path !== '#' && path !== '');
    return menuItemChildrenPaths.some((path) => toPathnameWithEndingSlash(currentPath) === toPathnameWithEndingSlash(path));
};
const isActiveOnHelpMenuItem = (menuItem, currentPath, app) => {
    var _a, _b;
    if (menuItem.path.includes('abi') && app === 'online-help' && currentPath.includes('abi')) {
        return true;
    }
    if (!menuItem.path.includes('abi') || !menuItem.children || currentPath.includes('era') || currentPath.includes('ari')) {
        return false;
    }
    const menuItemChildrenPaths = (((_b = (_a = menuItem.children[0]) === null || _a === void 0 ? void 0 : _a.children[0]) === null || _b === void 0 ? void 0 : _b.children) || []).map((item) => item.path);
    menuItemChildrenPaths.push('/abi');
    return !!menuItemChildrenPaths.find((path) => toPathnameWithEndingSlash(currentPath) === toPathnameWithEndingSlash(path));
};
const isIncludedInPath = (menuItem, currentPath) => {
    if (!menuItem.path && !menuItem.url) {
        return hasChildWithPath(menuItem, currentPath);
    }
    const localMenuItemPathname = resolveLocalMenuItemPathWithEndingSlash(menuItem);
    return localMenuItemPathname !== '/' && toPathnameWithEndingSlash(currentPath).includes(localMenuItemPathname);
};
const isMatchingPath = (menuItem, currentPath) => {
    const localMenuItemPathname = resolveLocalMenuItemPathWithEndingSlash(menuItem);
    return localMenuItemPathname !== '/' && toPathnameWithEndingSlash(currentPath).endsWith(localMenuItemPathname);
};
const resolveLocalMenuItemPathWithEndingSlash = (menuItem) => {
    const menuItemPathname = menuItem.path ? getPathnameWithoutLang(menuItem.path) : getPathnameWithoutLang(menuItem.url);
    return getWithEndingSlash(menuItemPathname);
};
const isSelfServiceAppAndSelfServiceMenuItem = (menuItem, app) => {
    return ['itb', 'selfservice'].includes(app) && isSelfServiceMenuItem(menuItem);
};
const isSelfServiceMenuItem = (menuItem) => {
    var _a;
    return (_a = menuItem.classes) === null || _a === void 0 ? void 0 : _a.includes('selfservice');
};
const isBillsMenuItem = (menuItem) => {
    var _a;
    return (_a = menuItem.classes) === null || _a === void 0 ? void 0 : _a.includes('bills-parent');
};
const isBillsChildMenuItem = (menuItem) => {
    var _a;
    return (_a = menuItem.classes) === null || _a === void 0 ? void 0 : _a.includes('bills');
};
const isContactsChildMenuItem = (menuItem) => {
    var _a;
    return (_a = menuItem.classes) === null || _a === void 0 ? void 0 : _a.includes('help-and-contacts');
};
const isShopAppAndShopMenuItem = (menuItem, app) => {
    return 'shop' === app && menuItem.classes && menuItem.classes.includes('shop');
};
const getMenuItemLabel = (menuItem, mobile = false) => {
    if (mobile) {
        return (menuItem.mobileLabel || menuItem.label || menuItem.title || '').trim();
    }
    return (menuItem.label || menuItem.title || '').trim();
};
const getMenuItemUrl = (menuItem, props) => {
    const path = menuItem.path || menuItem.url;
    return props.app !== 'public' && !path.startsWith('http') ? props.urlData.getPublicLink(path) : path;
};
//# sourceMappingURL=HeaderNavItemHelper.js.map